// IMPORTANT: Any non-essential cookie/tracker (analytics, marketing, embeds) must be
// conditionally loaded ONLY when consent === 'accepted'. Do not load before consent.
// The site currently uses NO analytics or third-party trackers. Only essential
// localStorage (the consent choice itself) is used.

import { createContext, useCallback, useContext, useEffect, useState } from 'react';

const STORAGE_KEY = 'jp_cookie_consent';

/**
 * Shape: { consent: 'accepted' | 'rejected' | null, accept, reject, reopen }
 */
const CookieConsentContext = createContext({
  consent: null,
  accept: () => {},
  reject: () => {},
  reopen: () => {},
});

export function CookieConsentProvider({ children }) {
  // Start as null on the server / pre-hydration; resolved from localStorage on mount.
  const [consent, setConsent] = useState(null);
  const [hydrated, setHydrated] = useState(false);

  useEffect(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored === 'accepted' || stored === 'rejected') {
        setConsent(stored);
      }
    } catch {
      /* localStorage unavailable (e.g. private mode). Treat as unset. */
    }
    setHydrated(true);
  }, []);

  const accept = useCallback(() => {
    try {
      localStorage.setItem(STORAGE_KEY, 'accepted');
    } catch {
      /* noop */
    }
    setConsent('accepted');
  }, []);

  const reject = useCallback(() => {
    try {
      localStorage.setItem(STORAGE_KEY, 'rejected');
    } catch {
      /* noop */
    }
    setConsent('rejected');
  }, []);

  /**
   * Clear stored choice and reset state so the banner reappears.
   * Called from the footer "Cookie settings" link.
   */
  const reopen = useCallback(() => {
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {
      /* noop */
    }
    setConsent(null);
  }, []);

  return (
    <CookieConsentContext.Provider value={{ consent, accept, reject, reopen, hydrated }}>
      {children}
    </CookieConsentContext.Provider>
  );
}

export function useCookieConsent() {
  return useContext(CookieConsentContext);
}
